import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import Quillwrapper from "components/quillwrapper";
import "react-quill/dist/quill.snow.css";

// === CONFIGURATION ET CONSTANTES CORRIGÉES ===
import {
  LOCALIZED_TEXTS,
  STYLE_PRESETS,
} from "config/editorConfig";
import { CUSTOM_STYLES } from "utils/editorStyles";
import {
  extractOutline,
  updateDocumentStats,
  getCurrentDateTime,
} from "utils/editorUtils";

// Configuration Quill corrigée et locale
const SAFE_EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["link", "image"],
    ["blockquote", "code-block"],
    ["clean"],
  ],
  keyboard: {
    bindings: {
      // Configuration minimaliste et sécurisée
      tab: {
        key: 9,
        handler: function(range, context) {
          if (range.length === 0 && context.empty) {
            this.quill.insertText(range.index, "    ");
            return false;
          }
          return true;
        }
      }
    }
  },
  clipboard: {
    matchVisual: false,
  },
  history: {
    delay: 1000,
    maxStack: 100,
    userOnly: true
  }
};

const SAFE_EDITOR_FORMATS = [
  "header",
  "font",
  "size", 
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet", 
  "indent",
  "link",
  "image",
  "color",
  "background",
  "align",
  "code-block",
];

// Constants for metadata
const CURRENT_DATE = getCurrentDateTime();

// Styles CSS étendus pour le preview
const EXTENDED_CUSTOM_STYLES = `
  ${CUSTOM_STYLES}
  
  /* Styles spécifiques pour le preview */
  .preview-mode {
    height: 100%;
    overflow-y: auto;
    background-color: #f8f9fa;
    padding: 20px;
  }
  
  .preview-page {
    max-width: 800px;
    margin: 0 auto;
    background: white;
    padding: 40px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    min-height: 600px;
  }
  
  /* Message de contenu vide */
  .empty-content-message {
    color: #9ca3af !important;
    font-style: italic !important;
    text-align: center !important;
    padding: 3em 0 !important;
    font-size: 1.1em !important;
  }

  /* Suppression des erreurs de script dans Quill */
  .ql-toolbar .ql-script {
    display: none !important;
  }
  
  .ql-snow .ql-tooltip[data-mode="script"] {
    display: none !important;
  }
`;

// === COMPOSANTS INTERNES (inchangés) ===

const NavigationPanel = ({
  navCollapsed,
  setNavCollapsed,
  documentOutline,
  navigationItems,
  showShortcuts,
  wordCount,
  charCount,
}) => (
  <div className={`navigation-panel ${navCollapsed ? "collapsed" : ""}`}>
    <div className="nav-header">
      <h4 className="nav-title">
        {!navCollapsed && LOCALIZED_TEXTS.navigation.title}
      </h4>
      <button
        className="nav-toggle"
        onClick={() => setNavCollapsed(!navCollapsed)}
        title={navCollapsed ? "Développer" : "Réduire"}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          {navCollapsed ? (
            <path d="m9 18 6-6-6-6" />
          ) : (
            <path d="m15 18-6-6 6-6" />
          )}
        </svg>
      </button>
    </div>

    {!navCollapsed && (
      <div className="nav-content">
        <DocumentOutline outline={documentOutline} />
        <NavigationItems items={navigationItems} />
        <ShortcutsList show={showShortcuts} />
        <StatisticsPanel
          wordCount={wordCount}
          charCount={charCount}
          sectionsCount={documentOutline.length}
        />
      </div>
    )}
  </div>
);

NavigationPanel.propTypes = {
  navCollapsed: PropTypes.bool.isRequired,
  setNavCollapsed: PropTypes.func.isRequired,
  documentOutline: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      level: PropTypes.number.isRequired,
      tag: PropTypes.string.isRequired,
    })
  ).isRequired,
  navigationItems: PropTypes.arrayOf(
    PropTypes.shape({
      section: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          icon: PropTypes.string.isRequired,
          active: PropTypes.bool.isRequired,
          onClick: PropTypes.func.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  showShortcuts: PropTypes.bool.isRequired,
  wordCount: PropTypes.number.isRequired,
  charCount: PropTypes.number.isRequired,
};

const DocumentOutline = ({ outline }) => {
  if (outline.length === 0) return null;

  return (
    <div className="nav-section">
      <div className="nav-section-title">
        {LOCALIZED_TEXTS.navigation.outline}
      </div>
      {outline.map((item) => (
        <div
          key={item.id}
          className="nav-item outline-item"
          style={{ "--level": item.level - 1 }}
        >
          <span className="nav-item-icon">{item.tag}</span>
          <span>{item.text}</span>
        </div>
      ))}
    </div>
  );
};

DocumentOutline.propTypes = {
  outline: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      level: PropTypes.number.isRequired,
      tag: PropTypes.string.isRequired,
    })
  ).isRequired,
};

const NavigationItems = ({ items }) => (
  <>
    {items.map((section) => (
      <div key={section.section} className="nav-section">
        <div className="nav-section-title">{section.section}</div>
        {section.items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`nav-item ${item.active ? "active" : ""}`}
            onClick={item.onClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                item.onClick();
              }
            }}
          >
            <span className="nav-item-icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    ))}
  </>
);

NavigationItems.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      section: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          icon: PropTypes.string.isRequired,
          active: PropTypes.bool.isRequired,
          onClick: PropTypes.func.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
};

const ShortcutsList = ({ show }) => {
  if (!show) return null;

  return (
    <div className="nav-section">
      <div className="nav-section-title">{LOCALIZED_TEXTS.shortcuts.title}</div>
      {Object.entries(LOCALIZED_TEXTS.shortcuts.categories).map(
        ([category, shortcuts]) => (
          <div key={category}>
            <div
              className="nav-item"
              style={{ fontSize: "10px", fontWeight: "bold", color: "#57a863" }}
            >
              {LOCALIZED_TEXTS.shortcuts[category] || category.toUpperCase()}
            </div>
            {shortcuts.slice(0, 4).map((shortcut) => (
              <div
                key={`${category}-${shortcut}`}
                className="nav-item"
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  paddingLeft: "32px",
                }}
              >
                <span>{shortcut}</span>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

ShortcutsList.propTypes = {
  show: PropTypes.bool.isRequired,
};

const StatisticsPanel = ({ wordCount, charCount, sectionsCount }) => (
  <div className="nav-section">
    <div className="nav-section-title">
      {LOCALIZED_TEXTS.navigation.statistics}
    </div>
    <div className="nav-item">
      <span className="nav-item-icon">📊</span>
      <span>{wordCount} mots</span>
    </div>
    <div className="nav-item">
      <span className="nav-item-icon">🔤</span>
      <span>{charCount} caractères</span>
    </div>
    <div className="nav-item">
      <span className="nav-item-icon">📑</span>
      <span>{sectionsCount} sections</span>
    </div>
  </div>
);

StatisticsPanel.propTypes = {
  wordCount: PropTypes.number.isRequired,
  charCount: PropTypes.number.isRequired,
  sectionsCount: PropTypes.number.isRequired,
};

const LanguageTabs = ({
  activeTab,
  handleTabChange,
  previewContent,
  setPreviewContent,
  insertFootnoteReference,
  onTabChangeWithSave,
}) => (
  <div className="language-tabs">
    {["fr", "en", "mg"].map((lang) => (
      <button
        key={lang}
        type="button"
        className={`language-tab ${activeTab.content === lang ? "active" : ""}`}
        onClick={() => onTabChangeWithSave(lang)}
      >
        {LOCALIZED_TEXTS.label[lang]}
      </button>
    ))}

    <ActionButtons
      previewContent={previewContent}
      setPreviewContent={setPreviewContent}
      insertFootnoteReference={insertFootnoteReference}
    />
  </div>
);

LanguageTabs.propTypes = {
  activeTab: PropTypes.shape({
    content: PropTypes.oneOf(["fr", "en", "mg"]).isRequired,
  }).isRequired,
  handleTabChange: PropTypes.func.isRequired,
  previewContent: PropTypes.bool.isRequired,
  setPreviewContent: PropTypes.func.isRequired,
  insertFootnoteReference: PropTypes.func.isRequired,
  onTabChangeWithSave: PropTypes.func.isRequired,
};

const ActionButtons = ({
  previewContent,
  setPreviewContent,
  insertFootnoteReference,
}) => (
  <div
    className="action-buttons"
    style={{ marginLeft: "auto", padding: "8px 16px" }}
  >
    <button
      type="button"
      className="btn-secondary"
      onClick={() => setPreviewContent(!previewContent)}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      {previewContent
        ? LOCALIZED_TEXTS.preview.hide
        : LOCALIZED_TEXTS.preview.show}
    </button>

    <button type="button" className="btn-primary">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
        <polyline points="17,21 17,13 7,13 7,21" />
        <polyline points="7,3 7,8 15,8" />
      </svg>
      {LOCALIZED_TEXTS.actions.save}
    </button>
  </div>
);

ActionButtons.propTypes = {
  previewContent: PropTypes.bool.isRequired,
  setPreviewContent: PropTypes.func.isRequired,
  insertFootnoteReference: PropTypes.func.isRequired,
};

const StylePresets = ({ selectedStyle, applyStyle }) => (
  <div className="editor-toolbar">
    <div className="toolbar-section">
      <span
        style={{
          fontSize: "12px",
          fontWeight: "600",
          color: "#64748b",
          marginRight: "16px",
        }}
      >
        Styles de document :
      </span>
      <div className="style-presets">
        {STYLE_PRESETS.map((style) => (
          <button
            key={style.id}
            type="button"
            className={`style-preset ${
              selectedStyle === style.id ? "style-preset-selected" : ""
            }`}
            onClick={() => applyStyle(style.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                applyStyle(style.id);
              }
            }}
            title={`${style.name} - ${style.description}`}
          >
            <div className="style-preset-preview">{style.preview}</div>
            <div className="style-preset-name">{style.name}</div>
          </button>
        ))}
      </div>
    </div>
  </div>
);

StylePresets.propTypes = {
  selectedStyle: PropTypes.string.isRequired,
  applyStyle: PropTypes.func.isRequired,
};

// === COMPOSANT CONTENTAREA CORRIGÉ ===
const ContentArea = ({
  activeTab,
  previewContent,
  formData,
  quillRef,
  setQuillRef,
  handleContentChange,
  currentContent,
}) => {
  // Fonction pour nettoyer et formater le contenu HTML pour le preview
  const formatPreviewContent = (htmlContent) => {
    if (
      !htmlContent ||
      htmlContent.trim() === "" ||
      htmlContent === "<p><br></p>"
    ) {
      return null;
    }

    // Nettoyer le contenu HTML si nécessaire
    let cleanContent = htmlContent;

    // Supprimer les balises vides superflues
    cleanContent = cleanContent.replace(/<p><br><\/p>/g, "");
    cleanContent = cleanContent.replace(/<p>\s*<\/p>/g, "");

    return cleanContent;
  };

  const getEmptyContentMessage = () => {
    return `<div class="empty-content-message">
      <p>${
        LOCALIZED_TEXTS.emptyContent[activeTab.content] ||
        "Aucun contenu disponible"
      }</p>
      <p style="font-size: 0.9em; margin-top: 1em;">Commencez à écrire dans l'éditeur pour voir votre contenu ici.</p>
    </div>`;
  };

  // CORRIGÉ: Gestionnaire d'erreur pour Quill
  const handleQuillError = useCallback((error) => {
    console.warn("⚠️ Erreur Quill ignorée:", error);
    // Ignorer les erreurs de script et de raccourcis clavier
    if (error.message?.includes('script') || error.message?.includes('keyboard')) {
      return;
    }
  }, []);

  return (
    <div style={{ flex: 1, overflow: "hidden" }}>
      {activeTab.content &&
        (previewContent ? (
          <div className="preview-mode">
            <div className="preview-page">
              <div
                className="ql-editor ql-editor-preview prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{
                  __html:
                    formatPreviewContent(currentContent) || getEmptyContentMessage(),
                }}
              />
            </div>
          </div>
        ) : (
          <div 
            onError={handleQuillError}
            style={{ height: "100%" }}
          >
            <Quillwrapper
              key={`quill-${activeTab.content}-safe`} // NOUVEAU: clé plus sûre
              ref={setQuillRef}
              value={currentContent || ""}
              onChange={(value) => {
                try {
                  handleContentChange(value, activeTab.content);
                } catch (error) {
                  console.warn("⚠️ Erreur lors du changement de contenu:", error);
                }
              }}
              placeholder={LOCALIZED_TEXTS.placeholder[activeTab.content]}
              theme="snow"
              modules={SAFE_EDITOR_MODULES} // MODIFIÉ: utilise la config sécurisée
              formats={SAFE_EDITOR_FORMATS} // MODIFIÉ: utilise les formats sécurisés
              style={{ height: "100%" }}
              onError={handleQuillError} // NOUVEAU: gestionnaire d'erreur
            />
          </div>
        ))}
    </div>
  );
};

ContentArea.propTypes = {
  activeTab: PropTypes.shape({
    content: PropTypes.oneOf(["fr", "en", "mg"]).isRequired,
  }).isRequired,
  previewContent: PropTypes.bool.isRequired,
  formData: PropTypes.shape({
    corp_art: PropTypes.shape({
      fr: PropTypes.string,
      en: PropTypes.string,
      mg: PropTypes.string,
    }).isRequired,
  }).isRequired,
  quillRef: PropTypes.object,
  setQuillRef: PropTypes.func.isRequired,
  handleContentChange: PropTypes.func.isRequired,
  currentContent: PropTypes.string,
};

const StatusBar = ({ wordCount, charCount, sectionsCount, activeTab }) => (
  <div className="status-bar">
    <div className="status-item">
      <div className="status-indicator"></div>
      <span>Sauvegarde automatique active</span>
    </div>
    <div className="status-item">
      <span>
        {wordCount} mots • {charCount} caractères • {sectionsCount} sections
      </span>
    </div>
    <div className="status-item">
      <span>{activeTab.content?.toUpperCase()}</span>
    </div>
  </div>
);

StatusBar.propTypes = {
  wordCount: PropTypes.number.isRequired,
  charCount: PropTypes.number.isRequired,
  sectionsCount: PropTypes.number.isRequired,
  activeTab: PropTypes.shape({
    content: PropTypes.oneOf(["fr", "en", "mg"]).isRequired,
  }).isRequired,
};

// === HOOKS PERSONNALISÉS OPTIMISÉS ===

const useEditorState = (formData, activeTab) => {
  const [selectedStyle, setSelectedStyle] = useState("normal");
  const [quillRef, setQuillRef] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [documentOutline, setDocumentOutline] = useState([]);
  
  // État local pour le contenu en cours d'édition
  const [localContent, setLocalContent] = useState({
    fr: "",
    en: "",
    mg: "",
  });

  // Refs pour éviter les boucles infinies
  const initializationRef = useRef(false);
  const formDataRef = useRef(null);

  // CORRIGÉ: Initialiser le contenu local UNIQUEMENT lors du premier rendu ou si formData change réellement
  useEffect(() => {
    if (!formData?.corp_art) return;

    // Vérifier si formData a réellement changé
    const formDataString = JSON.stringify(formData.corp_art);
    if (formDataRef.current === formDataString) return;

    formDataRef.current = formDataString;

    // Éviter les logs excessifs
    if (!initializationRef.current) {
      console.log("🔄 Initialisation du contenu local:", {
        timestamp: CURRENT_DATE,
        user: "HiQaody",
      });
    }

    setLocalContent({
      fr: formData.corp_art.fr || "",
      en: formData.corp_art.en || "",
      mg: formData.corp_art.mg || "",
    });

    initializationRef.current = true;
  }, [formData?.corp_art?.fr, formData?.corp_art?.en, formData?.corp_art?.mg]);

  // CORRIGÉ: Fonction updateStats mémorisée et sécurisée
  const updateStats = useCallback((content) => {
    if (!content) {
      setWordCount(0);
      setCharCount(0);
      setDocumentOutline([]);
      return;
    }

    try {
      const stats = updateDocumentStats(content);
      setWordCount(stats.wordCount);
      setCharCount(stats.charCount);
      setDocumentOutline(extractOutline(content));
    } catch (error) {
      console.warn("⚠️ Erreur lors du calcul des statistiques:", error);
      setWordCount(0);
      setCharCount(0);
      setDocumentOutline([]);
    }
  }, []);

  // CORRIGÉ: Mise à jour des stats uniquement quand le contenu change vraiment
  const currentContent = localContent[activeTab.content] || "";
  const currentContentRef = useRef("");

  useEffect(() => {
    if (currentContentRef.current !== currentContent) {
      currentContentRef.current = currentContent;
      updateStats(currentContent);
    }
  }, [currentContent, updateStats]);

  return {
    selectedStyle,
    setSelectedStyle,
    quillRef,
    setQuillRef,
    wordCount,
    charCount,
    documentOutline,
    updateStats,
    localContent,
    setLocalContent,
  };
};

const useEditorActions = (quillRef, handleInputChange, updateStats, localContent, setLocalContent) => {
  // Refs pour éviter les appels multiples
  const updateTimeoutRef = useRef(null);
  const lastSavedContentRef = useRef({});

  // CORRIGÉ: fonction de sauvegarde avec debounce et protection contre les logs excessifs
  const saveCurrentContent = useCallback((lang, content) => {
    // Éviter les sauvegardes inutiles
    if (lastSavedContentRef.current[lang] === content) return;

    lastSavedContentRef.current[lang] = content;

    // Log réduit
    if (Math.random() < 0.1) { // 10% de chance de logger
      console.log("💾 Sauvegarde du contenu:", {
        language: lang,
        contentLength: content?.length || 0,
        timestamp: CURRENT_DATE,
        user: "HiQaody",
      });
    }

    // Mettre à jour l'état local immédiatement
    setLocalContent(prev => ({
      ...prev,
      [lang]: content || "",
    }));

    // Débouncer la mise à jour de formData
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      handleInputChange("corp_art", lang, content || "");
    }, 300); // 300ms de délai

  }, [handleInputChange, setLocalContent]);

  // CORRIGÉ: fonction de changement de contenu optimisée avec logs réduits
  const handleContentChange = useCallback((value, lang) => {
    // Log réduit pour éviter le spam
    if (Math.random() < 0.05) { // 5% de chance de logger
      console.log("✏️ Changement de contenu:", {
        language: lang,
        contentLength: value?.length || 0,
        timestamp: CURRENT_DATE,
        user: "HiQaody",
      });
    }

    // Mettre à jour immédiatement l'état local
    setLocalContent(prev => ({
      ...prev,
      [lang]: value || "",
    }));

    // Mettre à jour les statistiques
    updateStats(value || "");

    // Débouncer la sauvegarde vers formData
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      handleInputChange("corp_art", lang, value || "");
      lastSavedContentRef.current[lang] = value || "";
    }, 500); // 500ms de délai pour l'édition

  }, [setLocalContent, updateStats, handleInputChange]);

  // CORRIGÉ: fonction pour changer d'onglet avec sauvegarde immédiate et protection d'erreurs
  const handleTabChangeWithSave = useCallback((currentLang, newLang, handleTabChange) => {
    console.log("🔄 Changement d'onglet avec sauvegarde:", {
      from: currentLang,
      to: newLang,
      timestamp: CURRENT_DATE,
      user: "HiQaody",
    });

    // Annuler le timeout en cours pour forcer la sauvegarde immédiate
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = null;
    }

    // Sauvegarder le contenu actuel immédiatement
    if (quillRef && currentLang) {
      try {
        const quill = quillRef.getEditor();
        if (quill && quill.root) {
          const currentContent = quill.root.innerHTML;
          
          // Sauvegarde immédiate
          handleInputChange("corp_art", currentLang, currentContent);
          lastSavedContentRef.current[currentLang] = currentContent;
          
          setLocalContent(prev => ({
            ...prev,
            [currentLang]: currentContent,
          }));
        }
      } catch (error) {
        console.warn("⚠️ Erreur lors de la sauvegarde:", error);
        // Fallback: utiliser le contenu local
        const currentContent = localContent[currentLang];
        if (currentContent && lastSavedContentRef.current[currentLang] !== currentContent) {
          handleInputChange("corp_art", currentLang, currentContent);
          lastSavedContentRef.current[currentLang] = currentContent;
        }
      }
    }

    // Changer d'onglet après la sauvegarde
    setTimeout(() => {
      handleTabChange("content", newLang);
    }, 50);

  }, [quillRef, handleInputChange, localContent, setLocalContent]);

  const applyStyle = useCallback((styleId) => {
    if (!quillRef) {
      console.warn("Quill editor not ready");
      return;
    }

    const style = STYLE_PRESETS.find((s) => s.id === styleId);
    if (!style?.apply) {
      console.warn("Style not found or no apply function", styleId);
      return;
    }

    try {
      const quill = quillRef.getEditor();
      let range = quill.getSelection();

      if (!range) {
        const [line] = quill.getLine(quill.getLength() - 1);
        if (line) {
          const lineIndex = quill.getIndex(line);
          const lineLength = line.length();
          range = { index: lineIndex, length: lineLength };
          quill.setSelection(range.index, range.length);
        }
      }

      style.apply(quill);
      console.log("🎨 Style appliqué:", {
        styleId,
        styleName: style.name,
        timestamp: CURRENT_DATE,
        user: "HiQaody",
      });

      quill.focus();
    } catch (error) {
      console.error("❌ Erreur lors de l'application du style:", error);
    }
  }, [quillRef]);

  const insertFootnoteReference = useCallback(() => {
    if (!quillRef) {
      console.warn("Quill editor not ready for footnote insertion");
      return;
    }

    try {
      const quill = quillRef.getEditor();
      const range = quill.getSelection(true);
      const footnoteNumber = Math.floor(Math.random() * 99) + 1; // Simulation

      // CORRIGÉ: Utiliser un format simple sans script
      quill.insertText(range.index, `[${footnoteNumber}]`, {
        color: "#57a863",
        bold: true,
      });

      console.log("📝 Note de bas de page insérée:", {
        footnoteNumber,
        position: range.index,
        timestamp: CURRENT_DATE,
        user: "HiQaody",
      });

      quill.focus();
    } catch (error) {
      console.error("❌ Erreur lors de l'insertion de la note:", error);
    }
  }, [quillRef]);

  // Nettoyage au démontage
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  return {
    handleContentChange,
    handleTabChangeWithSave,
    applyStyle,
    insertFootnoteReference,
    saveCurrentContent,
  };
};

// === COMPOSANT PRINCIPAL CORRIGÉ ===

const Step4Content = ({
  formData,
  handleInputChange,
  activeTab,
  handleTabChange,
  previewContent,
  setPreviewContent,
}) => {
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Hooks personnalisés - CORRIGÉS
  const {
    selectedStyle,
    setSelectedStyle,
    quillRef,
    setQuillRef,
    wordCount,
    charCount,
    documentOutline,
    updateStats,
    localContent,
    setLocalContent,
  } = useEditorState(formData, activeTab);

  const { 
    handleContentChange, 
    handleTabChangeWithSave, 
    applyStyle, 
    insertFootnoteReference 
  } = useEditorActions(quillRef, handleInputChange, updateStats, localContent, setLocalContent);

  // CORRIGÉ: fonction wrapper mémorisée pour le changement d'onglet
  const onTabChangeWithSave = useCallback((newLang) => {
    if (newLang === activeTab.content) return; // Éviter les changements inutiles
    handleTabChangeWithSave(activeTab.content, newLang, handleTabChange);
  }, [handleTabChangeWithSave, activeTab.content, handleTabChange]);

  // CORRIGÉ: Éléments de navigation mémorisés
  const navigationItems = useMemo(() => [
    {
      section: LOCALIZED_TEXTS.navigation.languages,
      items: [
        {
          id: "fr",
          label: LOCALIZED_TEXTS.label.fr,
          icon: "🇫🇷",
          active: activeTab.content === "fr",
          onClick: () => onTabChangeWithSave("fr"),
        },
        {
          id: "en",
          label: LOCALIZED_TEXTS.label.en,
          icon: "🇺🇸",
          active: activeTab.content === "en",
          onClick: () => onTabChangeWithSave("en"),
        },
        {
          id: "mg",
          label: LOCALIZED_TEXTS.label.mg,
          icon: "🇲🇬",
          active: activeTab.content === "mg",
          onClick: () => onTabChangeWithSave("mg"),
        },
      ],
    },
    {
      section: LOCALIZED_TEXTS.navigation.tools,
      items: [
        {
          id: "preview",
          label: previewContent
            ? LOCALIZED_TEXTS.preview.hide
            : LOCALIZED_TEXTS.preview.show,
          icon: previewContent ? "📝" : "👁️",
          active: previewContent,
          onClick: () => setPreviewContent(!previewContent),
        },
        {
          id: "shortcuts",
          label: "Raccourcis",
          icon: "⌨️",
          active: showShortcuts,
          onClick: () => setShowShortcuts(!showShortcuts),
        },
      ],
    },
  ], [activeTab.content, onTabChangeWithSave, previewContent, setPreviewContent, showShortcuts]);

  // CORRIGÉ: Effet pour détecter le style actuel (avec protection d'erreurs)
  useEffect(() => {
    if (!quillRef) return;

    try {
      const quill = quillRef.getEditor();
      if (!quill) return;

      const handleSelectionChange = (range) => {
        if (range) {
          try {
            const format = quill.getFormat(range);
            if (format.header) {
              setSelectedStyle(`heading${format.header}`);
            } else if (format.blockquote) {
              setSelectedStyle("quote");
            } else if (format["code-block"]) {
              setSelectedStyle("code");
            } else {
              setSelectedStyle("normal");
            }
          } catch (error) {
            console.warn("⚠️ Erreur lors de la détection du style:", error);
          }
        }
      };

      quill.on("selection-change", handleSelectionChange);
      return () => {
        try {
          quill.off("selection-change", handleSelectionChange);
        } catch (error) {
          console.warn("⚠️ Erreur lors du nettoyage des listeners:", error);
        }
      };
    } catch (error) {
      console.warn("⚠️ Erreur lors de l'initialisation des listeners:", error);
    }
  }, [quillRef]);

  // Contenu actuel
  const currentContent = localContent[activeTab.content] || "";

  return (
    <div className="step-content">
      <style>{EXTENDED_CUSTOM_STYLES}</style>

      <div className="mb-6">
        <h3 className="mb-2 text-2xl font-bold text-gray-800">
          Contenu de l'article
        </h3>
        <p className="text-gray-600">
          Rédigez et organisez le contenu de votre article avec les styles
          appropriés. Utilisez le mode aperçu pour visualiser le rendu final.
        </p>
      </div>

      <div className="editor-layout">
        <NavigationPanel
          navCollapsed={navCollapsed}
          setNavCollapsed={setNavCollapsed}
          documentOutline={documentOutline}
          navigationItems={navigationItems}
          showShortcuts={showShortcuts}
          wordCount={wordCount}
          charCount={charCount}
        />

        <div className="editor-area">
          <LanguageTabs
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            previewContent={previewContent}
            setPreviewContent={setPreviewContent}
            insertFootnoteReference={insertFootnoteReference}
            onTabChangeWithSave={onTabChangeWithSave}
          />

          {!previewContent && (
            <StylePresets
              selectedStyle={selectedStyle}
              applyStyle={applyStyle}
            />
          )}

          <ContentArea
            activeTab={activeTab}
            previewContent={previewContent}
            formData={formData}
            quillRef={quillRef}
            setQuillRef={setQuillRef}
            handleContentChange={handleContentChange}
            currentContent={currentContent}
          />

          <StatusBar
            wordCount={wordCount}
            charCount={charCount}
            sectionsCount={documentOutline.length}
            activeTab={activeTab}
          />
        </div>
      </div>
    </div>
  );
};

Step4Content.propTypes = {
  formData: PropTypes.shape({
    corp_art: PropTypes.shape({
      fr: PropTypes.string,
      en: PropTypes.string,
      mg: PropTypes.string,
    }).isRequired,
  }).isRequired,
  handleInputChange: PropTypes.func.isRequired,
  activeTab: PropTypes.shape({
    content: PropTypes.oneOf(["fr", "en", "mg"]).isRequired,
  }).isRequired,
  handleTabChange: PropTypes.func.isRequired,
  previewContent: PropTypes.bool.isRequired,
  setPreviewContent: PropTypes.func.isRequired,
};

export default Step4Content;